// Exports: todayISO, formatDateDDMMYY, priorityText, priorityToNumeric, toArray, getInitials, getAvatarColor, isCompletedStatus, isSubtaskCompleted, isTaskCompleted, getTaskDisplayStatus, deriveSubtaskStatus, mergeUniqueMembers, getMemberKey, getMemberAvatarUrl, getMemberDisplayName, findMatchingMember, resolveMemberAvatarUrl, resolveMemberDisplayName
export const todayISO = () => new Date().toISOString().slice(0, 10);

export const formatDateDDMMYY = (isoString) => {
  if (!isoString) return "—";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

export const priorityText = (value) =>
  ({ 3: "High", 2: "Medium", 1: "Low", High: "High", Medium: "Medium", Low: "Low" }[
    value
  ] || "Medium");

export const priorityToNumeric = {
  High: 3,
  Medium: 2,
  Low: 1,
  NotSet: 0,
};

export const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.task_groups) return raw.task_groups;
  if (raw?.tasks) return raw.tasks;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.data?.results)) return raw.data.results;
  return [];
};

export const getInitials = (name) => {
  if (!name || name === "Unassigned") return "?";

  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const getAvatarColor = (name) => {
  const colors = [
    "bg-blue-800",
    "bg-purple-800",
    "bg-green-700",
    "bg-red-700",
    "bg-yellow-700",
    "bg-pink-700",
    "bg-indigo-700",
  ];

  const hash = [...(name || "?")].reduce(
    (runningTotal, character) => runningTotal + character.charCodeAt(0),
    0
  );

  return colors[hash % colors.length];
};

export const isCompletedStatus = (status) =>
  status === "Completed" || status === "Complete";

export const isSubtaskCompleted = (subtask) =>
  Boolean(subtask?.is_completed) || isCompletedStatus(subtask?.status);

export const isTaskCompleted = (task) => {
  if (!task) return false;
  if (Boolean(task.is_completed) || isCompletedStatus(task.status)) return true;

  const subtasks = Array.isArray(task.subtasks) ? task.subtasks : [];
  return subtasks.length > 0 && subtasks.every(isSubtaskCompleted);
};

export const getTaskDisplayStatus = (task) =>
  isTaskCompleted(task) ? "Completed" : task?.status;

export const deriveSubtaskStatus = (subtask, taskStatus) => {
  if (Boolean(subtask?.is_completed)) return "Completed";
  if (taskStatus === "Not started") return "Not started";

  const now = new Date();
  const startDate = new Date(subtask?.start_date);
  const endDate = new Date(subtask?.end_date);

  if (!Number.isNaN(startDate.getTime()) && startDate > now) {
    return "Not started";
  }

  if (!Number.isNaN(endDate.getTime()) && endDate < now) {
    return "Overdue";
  }

  return "Ongoing";
};

export const mergeUniqueMembers = (...memberLists) => {
  const members = new Map();

  memberLists
    .flat()
    .filter(Boolean)
    .forEach((member) => {
      const key = member?.member_id ?? member?.id;
      if (key == null || members.has(key)) return;
      members.set(key, member);
    });

  return Array.from(members.values());
};

const normalizeComparableId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const collectMemberIds = (member) =>
  [
    member?.member_id,
    member?.id,
    member?.organization_member_id,
    member?.team_member_id,
  ]
    .map(normalizeComparableId)
    .filter(Boolean);

const collectUserIds = (member) =>
  [
    member?.user_id,
    member?.user?.id,
    member?.user?.user_id,
    member?.user_details?.id,
    member?.user_details?.user_id,
  ]
    .map(normalizeComparableId)
    .filter(Boolean);

const hasSharedId = (leftIds, rightIds) => {
  if (!leftIds.length || !rightIds.length) return false;
  const rightSet = new Set(rightIds);
  return leftIds.some((id) => rightSet.has(id));
};

export const getMemberKey = (member, fallback = "") =>
  String(
    member?.member_id ??
      member?.id ??
      member?.organization_member_id ??
      member?.team_member_id ??
      member?.user_id ??
      member?.user?.id ??
      fallback,
  );

export const getMemberAvatarUrl = (member) =>
  member?.userpic ||
  member?.profile_pic ||
  member?.profilePic ||
  member?.avatar ||
  member?.user?.userpic ||
  member?.user_details?.userpic ||
  "";

export const getMemberDisplayName = (member, fallback = "") => {
  const firstName =
    member?.first_name ||
    member?.user?.first_name ||
    member?.user_details?.first_name ||
    "";
  const lastName =
    member?.last_name ||
    member?.user?.last_name ||
    member?.user_details?.last_name ||
    "";
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    fullName ||
    member?.name ||
    member?.user?.name ||
    member?.user_details?.name ||
    member?.email ||
    member?.user?.email ||
    member?.user_details?.email ||
    fallback
  );
};

export const findMatchingMember = (member, memberList = []) => {
  const memberIds = collectMemberIds(member);
  const userIds = collectUserIds(member);

  return (
    memberList.find((candidate) =>
      hasSharedId(memberIds, collectMemberIds(candidate)),
    ) ||
    memberList.find((candidate) =>
      hasSharedId(userIds, collectUserIds(candidate)),
    ) ||
    null
  );
};

export const resolveMemberAvatarUrl = (member, memberList = []) => {
  const matchingMember = findMatchingMember(member, memberList);
  return getMemberAvatarUrl(matchingMember) || getMemberAvatarUrl(member);
};

export const resolveMemberDisplayName = (member, memberList = []) => {
  const matchingMember = findMatchingMember(member, memberList);
  return (
    getMemberDisplayName(member) ||
    getMemberDisplayName(matchingMember) ||
    "Member"
  );
};
